import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import subscriptionApi from "../../api/subscription";
import { toast } from "react-toastify";
import {
  CreditCard, Smartphone, Building2, ArrowRight, ArrowLeft,
  CheckCircle2, ShieldCheck, Zap, AlertCircle, Info, Landmark,
  ChevronRight, Loader2
} from "lucide-react";

// ─── Utility ──────────────────────────────────────────────────────────────────
const formatPhone = (raw) => {
  let v = String(raw || "").trim().replace(/\D/g, "");
  if (v.startsWith("0") && v.length > 1) v = "254" + v.slice(1);
  else if ((v.startsWith("7") || v.startsWith("1")) && v.length <= 9) v = "254" + v;
  return v;
};

const BANKS = [
  { value: "I&M Bank", label: "I&M Bank", code: "57" },
  { value: "Equity Bank", label: "Equity Bank", code: "68" },
  { value: "Co-operative Bank", label: "Co-operative Bank", code: "11" },
  { value: "Absa Bank", label: "Absa Bank", code: "03" },
  { value: "Stanbic Bank", label: "Stanbic Bank", code: "31" },
  { value: "NCBA Bank", label: "NCBA Bank", code: "07" },
  { value: "Diamond Trust Bank", label: "Diamond Trust Bank (DTB)", code: "63" },
  { value: "Family Bank", label: "Family Bank", code: "70" },
  { value: "Other Bank", label: "Other Bank (PesaLink / EFT)", code: "99" },
];

// ─── Sub-components ────────────────────────────────────────────────────────────
const StepIndicator = ({ currentStep, steps }) => (
  <div className="flex items-center justify-center gap-0 mb-8 select-none">
    {steps.map((label, i) => {
      const step = i + 1;
      const isActive = step === currentStep;
      const isDone = step < currentStep;
      return (
        <div key={step} className="flex items-center">
          <div className="flex flex-col items-center gap-1">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
              isDone ? "bg-emerald-500 text-white shadow-md shadow-emerald-200" :
              isActive ? "bg-indigo-600 text-white shadow-md shadow-indigo-200 ring-4 ring-indigo-100" :
              "bg-gray-100 text-gray-400"
            }`}>
              {isDone ? <CheckCircle2 className="w-5 h-5" /> : step}
            </div>
            <span className={`text-xs font-medium hidden sm:block ${isActive ? "text-indigo-700" : isDone ? "text-emerald-600" : "text-gray-400"}`}>
              {label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`w-16 sm:w-24 h-0.5 mx-1 transition-all duration-500 ${step < currentStep ? "bg-emerald-400" : "bg-gray-200"}`} />
          )}
        </div>
      );
    })}
  </div>
);

const GatewayCard = ({ id, icon: Icon, title, subtitle, badge, color, selected, onClick }) => (
  <button
    id={id}
    type="button"
    onClick={onClick}
    className={`relative w-full p-4 rounded-2xl border-2 text-left transition-all duration-200 group ${
      selected
        ? `border-${color}-500 bg-${color}-50 shadow-md shadow-${color}-100 ring-2 ring-${color}-200`
        : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
    }`}
  >
    {badge && (
      <span className={`absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${
        badge === "UAT" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
      }`}>
        {badge}
      </span>
    )}
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-colors ${
      selected ? `bg-${color}-100` : "bg-gray-100 group-hover:bg-gray-200"
    }`}>
      <Icon className={`w-5 h-5 ${selected ? `text-${color}-600` : "text-gray-500"}`} />
    </div>
    <div className={`font-bold text-sm ${selected ? `text-${color}-800` : "text-gray-800"}`}>{title}</div>
    <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>
    {selected && (
      <CheckCircle2 className={`absolute bottom-3 right-3 w-4 h-4 text-${color}-500`} />
    )}
  </button>
);

const ChannelCard = ({ value, icon: Icon, title, subtitle, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full p-3.5 rounded-xl border text-left transition-all duration-200 flex items-start gap-3 ${
      selected
        ? "border-indigo-500 bg-indigo-50 shadow-sm ring-1 ring-indigo-300"
        : "border-gray-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/30"
    }`}
  >
    <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${selected ? "bg-indigo-100" : "bg-gray-100"}`}>
      <Icon className={`w-4 h-4 ${selected ? "text-indigo-600" : "text-gray-500"}`} />
    </div>
    <div>
      <div className={`font-semibold text-sm ${selected ? "text-indigo-800" : "text-gray-800"}`}>{title}</div>
      <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>
    </div>
    {selected && <CheckCircle2 className="ml-auto mt-0.5 w-4 h-4 text-indigo-500 flex-shrink-0" />}
  </button>
);

const InputField = ({ label, id, helper, error, ...props }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
    <input
      id={id}
      className={`w-full px-4 py-3 rounded-xl border text-sm transition-all outline-none focus:ring-2 ${
        error ? "border-red-400 focus:ring-red-200 bg-red-50" : "border-gray-300 focus:ring-indigo-200 focus:border-indigo-400 bg-white"
      }`}
      {...props}
    />
    {error && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
    {helper && !error && <p className="text-gray-400 text-xs mt-1.5">{helper}</p>}
  </div>
);

const InfoBox = ({ color = "blue", icon: Icon = Info, children }) => {
  const colors = {
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    green: "bg-emerald-50 border-emerald-200 text-emerald-800",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-800",
  };
  return (
    <div className={`rounded-xl border p-3.5 flex gap-2.5 text-xs leading-relaxed ${colors[color]}`}>
      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const SubscriptionPaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeGateways, setActiveGateways] = useState(["daraja", "kcb", "stripe"]);
  const [paymentMethod, setPaymentMethod] = useState("kcb");
  const [kcbChannel, setKcbChannel] = useState("kcb_account");
  const [kcbEnabledChannels, setKcbEnabledChannels] = useState({
    kcb_account: true,
    mpesa_to_kcb: true,
    other_bank_to_kcb: true,
  });
  const [phoneNumber, setPhoneNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [sourceBank, setSourceBank] = useState("I&M Bank");
  const [errors, setErrors] = useState({});

  const { plan } = location.state || {};

  useEffect(() => {
    if (!plan) navigate("/pricing");
  }, [plan, navigate]);

  useEffect(() => {
    const fetchGateways = async () => {
      try {
        const res = await subscriptionApi.getActiveGateways();
        if (res.data?.enabled_codes) {
          const codes = res.data.enabled_codes.map((c) => (c === "mpesa" ? "daraja" : c));
          setActiveGateways(codes);
          if (codes.length > 0 && !codes.includes(paymentMethod)) setPaymentMethod(codes[0]);
        }
        if (res.data?.gateways) {
          const kcbGw = res.data.gateways.find((g) => g.gateway === "kcb");
          if (kcbGw?.config_data?.enabled_channels) {
            const channels = kcbGw.config_data.enabled_channels;
            setKcbEnabledChannels(channels);
            if (channels[kcbChannel] === false) {
              const available = ["kcb_account", "mpesa_to_kcb", "other_bank_to_kcb"].find(
                (ch) => channels[ch] !== false
              );
              if (available) setKcbChannel(available);
            }
          }
        }
      } catch (_) {}
    };
    fetchGateways();
  }, []);

  const validateStep2 = useCallback(() => {
    const errs = {};
    if (paymentMethod === "daraja") {
      if (!phoneNumber.trim()) errs.phoneNumber = "M-Pesa phone number is required";
      else if (!/^254[17]\d{8}$/.test(formatPhone(phoneNumber)))
        errs.phoneNumber = "Enter a valid Kenyan number (07XX or 01XX)";
    } else if (paymentMethod === "kcb") {
      if (kcbChannel === "mpesa_to_kcb") {
        if (!phoneNumber.trim()) errs.phoneNumber = "M-Pesa phone number is required";
        else if (!/^254[17]\d{8}$/.test(formatPhone(phoneNumber)))
          errs.phoneNumber = "Enter a valid Kenyan number (07XX or 01XX)";
      } else if (kcbChannel === "kcb_account") {
        if (!accountNumber.trim()) errs.accountNumber = "KCB account number is required";
      } else if (kcbChannel === "other_bank_to_kcb") {
        if (!accountNumber.trim()) errs.accountNumber = "Sender account / reference is required";
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [paymentMethod, kcbChannel, phoneNumber, accountNumber]);

  const handleNext = () => {
    if (step === 1) { setErrors({}); setStep(2); return; }
    if (step === 2 && validateStep2()) { setStep(3); return; }
  };

  const handleBack = () => setStep((s) => Math.max(1, s - 1));

  const handlePayment = async () => {
    if (!validateStep2()) { setStep(2); return; }
    setIsProcessing(true);
    try {
      const payload = { plan_id: plan.id, payment_method: paymentMethod };

      if (paymentMethod === "daraja") {
        payload.phone_number = formatPhone(phoneNumber);
      } else if (paymentMethod === "kcb") {
        payload.channel = kcbChannel;
        payload.phone_number = formatPhone(phoneNumber);
        payload.account_number = accountNumber || `KCB-REF-${Date.now().toString().slice(-6)}`;
        payload.source_bank = sourceBank;
        payload.bank_code = BANKS.find((b) => b.value === sourceBank)?.code || "99";
        payload.swift_code = "KCBLKENX";
        payload.is_international = false;
      }

      const response = await subscriptionApi.initiatePayment(payload);

      if (response.status === 200 || response.status === 201) {
        const result = response.data;

        if (paymentMethod === "daraja" || (paymentMethod === "kcb" && result?.checkout_request_id)) {
          toast.success(result?.customer_message || "M-Pesa STK Push sent! Check your phone.");
          navigate("/subscription/pending", {
            state: {
              plan,
              paymentMethod,
              kcbChannel,
              checkoutRequestId: result?.checkout_request_id,
              merchantRequestId: result?.merchant_request_id,
              subscriptionId: result?.subscription_id,
              customerMessage: result?.customer_message,
            },
          });
        } else if (paymentMethod === "kcb") {
          toast.info("Payment instructions ready — complete your transfer to activate.");
          navigate("/subscription/pending", {
            state: {
              plan,
              paymentMethod: "kcb",
              kcbChannel,
              reference: result?.reference,
              checkoutRequestId: result?.reference,
              instructions: result?.instructions,
              isIpn: true,
              channel: result?.channel,
              sourceBank: result?.source_bank,
              amount: result?.amount,
              currency: result?.currency,
              subscriptionId: result?.subscription_id,
            },
          });
        } else if (paymentMethod === "stripe") {
          if (result.client_secret) {
            navigate("/subscription/stripe-checkout", {
              state: { planName: plan.name, planPrice: plan.price, clientSecret: result.client_secret },
            });
          } else throw new Error("Error initiating Stripe payment.");
        }
      } else {
        throw new Error(response.data?.error || "Payment initiation failed");
      }
    } catch (error) {
      setErrors({ submit: error.response?.data?.error || error.message || "Failed to initiate payment." });
      setStep(3);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!plan) return null;

  const isMpesaEnabled = activeGateways.includes("daraja") || activeGateways.includes("mpesa");
  const isKcbEnabled = activeGateways.includes("kcb");
  const isStripeEnabled = activeGateways.includes("stripe");

  const allKcbChannels = [
    { value: "kcb_account", icon: Landmark, title: "KCB Account → KCB" },
    { value: "mpesa_to_kcb", icon: Smartphone, title: "M-Pesa → KCB" },
    { value: "other_bank_to_kcb", icon: Building2, title: "Other Bank → KCB" },
  ];
  const availableKcbChannels = allKcbChannels.filter(
    (ch) => kcbEnabledChannels[ch.value] !== false
  );

  const isStepComplete1 = true;
  const isStepComplete2 = step >= 3;

  // ─── Step 1: Select Gateway ──────────────────────────────────────────────────
  const renderStep1 = () => (
    <div className="space-y-4 animate-fadeIn">
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-1">Choose Payment Method</h2>
        <p className="text-sm text-gray-500">Select how you'd like to pay for your subscription.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {isKcbEnabled && (
          <GatewayCard
            id="gw-kcb"
            icon={Landmark}
            title="KCB Bank"
            subtitle="M-Pesa, interbank, KCB account"
            color="indigo"
            selected={paymentMethod === "kcb"}
            onClick={() => setPaymentMethod("kcb")}
          />
        )}
        {isMpesaEnabled && (
          <GatewayCard
            id="gw-mpesa"
            icon={Smartphone}
            title="M-Pesa Direct"
            subtitle="Safaricom STK Push"
            color="emerald"
            selected={paymentMethod === "daraja"}
            onClick={() => setPaymentMethod("daraja")}
          />
        )}
        {isStripeEnabled && (
          <GatewayCard
            id="gw-stripe"
            icon={CreditCard}
            title="Card (Stripe)"
            subtitle="Visa / Mastercard / Amex"
            color="purple"
            selected={paymentMethod === "stripe"}
            onClick={() => setPaymentMethod("stripe")}
          />
        )}
      </div>

      {paymentMethod === "stripe" && (
        <InfoBox color="indigo" icon={ShieldCheck}>
          Card payments are processed securely via <strong>Stripe</strong>. You'll be redirected to a secure checkout form.
        </InfoBox>
      )}
      {paymentMethod === "daraja" && (
        <InfoBox color="green" icon={Smartphone}>
          A <strong>Safaricom M-Pesa STK push</strong> will be sent to your registered number. Enter your PIN to pay.
        </InfoBox>
      )}
      {paymentMethod === "kcb" && (
        <InfoBox color="indigo" icon={Landmark}>
          KCB Bank supports multiple payment channels. You can pay via M-Pesa (STK push), KCB account transfer, or from another bank.
        </InfoBox>
      )}
    </div>
  );

  // ─── Step 2: Configure Details ───────────────────────────────────────────────
  const renderStep2 = () => (
    <div className="space-y-5 animate-fadeIn">
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-1">Payment Details</h2>
        <p className="text-sm text-gray-500">
          {paymentMethod === "daraja" && "Enter your M-Pesa number to receive the STK push."}
          {paymentMethod === "kcb" && "Choose your KCB payment channel and fill in the required details."}
          {paymentMethod === "stripe" && "You'll be redirected to complete card payment."}
        </p>
      </div>

      {/* M-Pesa Direct */}
      {paymentMethod === "daraja" && (
        <div className="space-y-4">
          <InputField
            label="M-Pesa Phone Number"
            id="mpesa-phone"
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="0712 345 678"
            error={errors.phoneNumber}
            helper="Format: 07XX XXX XXX or 01XX XXX XXX"
          />
          <InfoBox color="green" icon={Smartphone}>
            <strong>How it works:</strong> After clicking Pay, an M-Pesa notification will appear on <strong>{formatPhone(phoneNumber) || "your phone"}</strong>. Enter your PIN within 60 seconds to complete payment.
          </InfoBox>
        </div>
      )}

      {/* Stripe — no input needed */}
      {paymentMethod === "stripe" && (
        <InfoBox color="indigo" icon={CreditCard}>
          Click <strong>Continue to Card Payment</strong> to be redirected to Stripe's secure checkout. Your card details are never stored on our servers.
        </InfoBox>
      )}

      {/* KCB Bank Channels */}
      {paymentMethod === "kcb" && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Select KCB Payment Channel</label>
            {availableKcbChannels.length > 0 ? (
              <div className={`grid grid-cols-1 ${availableKcbChannels.length === 2 ? 'sm:grid-cols-2' : availableKcbChannels.length >= 3 ? 'sm:grid-cols-3' : ''} gap-2.5`}>
                {availableKcbChannels.map((ch) => (
                  <ChannelCard
                    key={ch.value}
                    value={ch.value}
                    icon={ch.icon}
                    title={ch.title}
                    selected={kcbChannel === ch.value}
                    onClick={() => setKcbChannel(ch.value)}
                  />
                ))}
              </div>
            ) : (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 text-amber-600" />
                No payment channels are currently enabled for KCB Bank. Please select another payment method.
              </div>
            )}
          </div>

          {/* Channel 1: M-Pesa → KCB */}
          {kcbChannel === "mpesa_to_kcb" && (
            <div className="space-y-3 bg-indigo-50/60 border border-indigo-100 rounded-2xl p-4">
              <InputField
                label="M-Pesa Phone Number"
                id="kcb-mpesa-phone"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="0712 345 678"
                error={errors.phoneNumber}
                helper="An STK push will be sent to this phone number to authorise payment."
              />
            </div>
          )}

          {/* Channel 2: Other Bank → KCB (IPN) */}
          {kcbChannel === "other_bank_to_kcb" && (
            <div className="space-y-3 bg-blue-50/60 border border-blue-100 rounded-2xl p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Your Bank</label>
                  <select
                    value={sourceBank}
                    onChange={(e) => setSourceBank(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                  >
                    {BANKS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </select>
                </div>
                <InputField
                  label="Sender Account / Ref"
                  id="other-bank-acc"
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="Your sending account number"
                  error={errors.accountNumber}
                />
              </div>
            </div>
          )}

          {/* Channel 3: KCB Account */}
          {kcbChannel === "kcb_account" && (
            <div className="space-y-3 bg-indigo-50/60 border border-indigo-100 rounded-2xl p-4">
              <InputField
                label="Your KCB Account Number"
                id="kcb-acc"
                type="text"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                placeholder="e.g. 1102839481"
                error={errors.accountNumber}
                helper="Funds will be transferred from your KCB account."
              />
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ─── Step 3: Review & Pay ────────────────────────────────────────────────────
  const getMethodLabel = () => {
    if (paymentMethod === "daraja") return "M-Pesa Direct (Safaricom STK Push)";
    if (paymentMethod === "stripe") return "Card Payment (Stripe)";
    if (paymentMethod === "kcb") {
      const labels = {
        mpesa_to_kcb: "KCB Bank — M-Pesa STK Push (Buni API)",
        other_bank_to_kcb: `KCB Bank — Interbank Transfer from ${sourceBank} (IPN)`,
        kcb_account: "KCB Bank — Account Transfer",
      };
      return labels[kcbChannel] || "KCB Bank";
    }
    return paymentMethod;
  };

  const getPaymentDetail = () => {
    if (paymentMethod === "daraja") return formatPhone(phoneNumber) || "—";
    if (paymentMethod === "kcb") {
      if (kcbChannel === "mpesa_to_kcb") return formatPhone(phoneNumber) || "—";
      if (kcbChannel === "kcb_account") return accountNumber || "—";
      if (kcbChannel === "other_bank_to_kcb") return `${sourceBank}${accountNumber ? ` · ${accountNumber}` : ""}`;
    }
    return "—";
  };

  const renderStep3 = () => (
    <div className="space-y-5 animate-fadeIn">
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-1">Review & Pay</h2>
        <p className="text-sm text-gray-500">Confirm your order before completing payment.</p>
      </div>

      {/* Order Summary */}
      <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-2xl p-5 text-white">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-indigo-200 text-xs font-medium uppercase tracking-wide mb-1">Subscription Plan</p>
            <p className="text-xl font-bold">{plan.name}</p>
            <p className="text-indigo-200 text-sm mt-0.5">{plan.description}</p>
          </div>
          <div className="text-right">
            <p className="text-indigo-200 text-xs">Amount</p>
            <p className="text-3xl font-black">KES {Number(plan.price).toLocaleString()}</p>
            <p className="text-indigo-300 text-xs">per month</p>
          </div>
        </div>
      </div>

      {/* Payment Summary */}
      <div className="bg-gray-50 rounded-2xl border border-gray-200 divide-y divide-gray-200">
        <div className="px-4 py-3 flex justify-between text-sm">
          <span className="text-gray-500">Payment method</span>
          <span className="font-medium text-gray-800 text-right max-w-[60%]">{getMethodLabel()}</span>
        </div>
        <div className="px-4 py-3 flex justify-between text-sm">
          <span className="text-gray-500">{paymentMethod === "stripe" ? "Card details" : "Phone / Account"}</span>
          <span className="font-mono font-medium text-gray-800">{getPaymentDetail()}</span>
        </div>
        <div className="px-4 py-3 flex justify-between text-sm font-bold">
          <span className="text-gray-700">Total Due</span>
          <span className="text-indigo-700 text-base">KES {Number(plan.price).toLocaleString()}</span>
        </div>
      </div>

      {/* Trust badges */}
      <div className="flex items-center justify-center gap-6 py-2">
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <ShieldCheck className="w-4 h-4 text-emerald-400" />
          SSL Encrypted
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Zap className="w-4 h-4 text-amber-400" />
          Instant Activation
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <CheckCircle2 className="w-4 h-4 text-blue-400" />
          Cancel Anytime
        </div>
      </div>

      {errors.submit && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3.5 flex items-start gap-2.5 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          {errors.submit}
        </div>
      )}
    </div>
  );

  const payButtonLabel = () => {
    if (isProcessing) return (
      <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Processing...</span>
    );
    if (paymentMethod === "stripe") return `Continue to Card Payment →`;
    if (paymentMethod === "kcb" && kcbChannel !== "mpesa_to_kcb") return `Get Transfer Instructions`;
    return `Pay KES ${Number(plan.price).toLocaleString()} →`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-slate-100 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-1.5 shadow-sm border border-gray-200 mb-4">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            <span className="text-xs font-semibold text-gray-600">Secure Checkout</span>
          </div>
          <h1 className="text-2xl font-black text-gray-900">Complete Your Subscription</h1>
          <p className="text-gray-500 text-sm mt-1">
            Subscribing to <span className="font-semibold text-indigo-600">{plan.name}</span>
          </p>
        </div>

        {/* Step Indicator */}
        <StepIndicator currentStep={step} steps={["Gateway", "Details", "Review & Pay"]} />

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-8">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}

          {/* Navigation Buttons */}
          <div className={`mt-6 flex gap-3 ${step > 1 ? "justify-between" : "justify-end"}`}>
            {step > 1 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={isProcessing}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 hover:bg-gray-50 transition-all disabled:opacity-50"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}

            {step < 3 ? (
              <button
                type="button"
                onClick={handleNext}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold shadow-md shadow-indigo-200 transition-all hover:shadow-lg hover:shadow-indigo-200 active:scale-95"
              >
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handlePayment}
                disabled={isProcessing}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white text-sm font-bold shadow-lg shadow-indigo-300 transition-all hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed active:scale-95"
              >
                {payButtonLabel()}
              </button>
            )}
          </div>
        </div>

        {/* Back to plans */}
        <div className="text-center mt-4">
          <button
            onClick={() => navigate("/pricing")}
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            ← Back to Plans
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPaymentPage;